import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  queryKeysSavedFilter,
  type QuerySavedFiltersResponse,
  SAVED_FILTER_NAME_MAX_LENGTH,
  upsertCreatedSavedFilterInCache,
  useMutationCreateSavedFilter,
  useMutationDeleteSavedFilter,
  useMutationUpdateSavedFilter,
} from '~/data/savedFilter';

import ToastService from '~/services/toast.service';

import { hasHttpStatus } from '~/utils/httpError';

import { Icon, IconButton } from '@vestigas/ui';

import { useUniversalFiltersSelector } from '../../hooks/useUniversalFiltersSelector';
import { type BackendSavedFilter, UNIVERSAL_FILTER_ALL_ID } from '../../types';
import { useUniversalFiltersContext } from '../../UniversalFiltersProvider';
import { SavedFilterNameDialog } from '../SavedFilterNameDialog';

import { SavedFilterBadge } from './SavedFilterBadge';
import { SavedFilterOverflowMenu } from './SavedFilterOverflowMenu';
import {
  buildFilterBadges,
  getFilterBadgeState,
} from './savedFiltersBadgeRowUtils';
import { useBadgeRowOverflow } from './useBadgeRowOverflow';

/**
 * Renders the built-in All tab and backend saved-filter tabs when enabled.
 *
 * Backend tabs are user-managed: the selected persisted filter exposes rename
 * and delete actions independently of whether a drawer is mounted.
 */
export function SavedFiltersBadgeRow() {
  const { t } = useTranslation('universalFilters');
  const queryClient = useQueryClient();
  const { config, savedFiltersState, store } = useUniversalFiltersContext();
  const [editingFilter, setEditingFilter] = useState<BackendSavedFilter>();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const builtInFilters = useUniversalFiltersSelector(
    (context) => context.builtInFilters,
  );
  const isDirty = useUniversalFiltersSelector((context) => context.isDirty);
  const resultCountsByFilterId = useUniversalFiltersSelector(
    (context) => context.resultCountsByFilterId,
  );
  const selectedFilterId = useUniversalFiltersSelector(
    (context) => context.selectedFilterId,
  );

  const createSavedFilterMutation = useMutationCreateSavedFilter({
    onSuccess(savedFilter, variables) {
      queryClient.setQueryData<QuerySavedFiltersResponse>(
        queryKeysSavedFilter.getAll(variables.type),
        (previousCache) =>
          upsertCreatedSavedFilterInCache(previousCache, savedFilter),
      );
      store.trigger.saveSelectedFilterSucceeded({ savedFilter });
      ToastService.success([
        t(($) => $.savedFilters.create.notifications.success),
      ]);
      setIsCreateDialogOpen(false);
    },
    onError(error) {
      ToastService.error([
        hasHttpStatus(error, 409)
          ? t(($) => $.savedFilters.create.notifications.conflict)
          : t(($) => $.savedFilters.create.notifications.error),
      ]);
    },
  });
  const updateSavedFilterMutation = useMutationUpdateSavedFilter({
    onSuccess() {
      ToastService.success([t(($) => $.savedFilters.edit.successToast)]);
      setEditingFilter(undefined);
    },
    onError(error) {
      ToastService.error([
        hasHttpStatus(error, 409)
          ? t(($) => $.savedFilters.edit.conflictToast)
          : t(($) => $.savedFilters.edit.errorToast),
      ]);
    },
  });
  const deleteSavedFilterMutation = useMutationDeleteSavedFilter({
    onSuccess() {
      ToastService.success([t(($) => $.savedFilters.edit.delete.successToast)]);
      setEditingFilter(undefined);
      store.trigger.selectFilter({
        baseline: [],
        filterId: UNIVERSAL_FILTER_ALL_ID,
      });
    },
    onError() {
      ToastService.error([t(($) => $.savedFilters.edit.delete.errorToast)]);
    },
  });

  const savedFilterIds = new Set(
    savedFiltersState.filters.map((filter) => filter.id),
  );

  const allLabel = t(($) => $.savedFilters.allLabel);
  // Memoized so useBadgeRowOverflow's recompute keeps a stable identity —
  // otherwise the ResizeObserver is torn down and rebuilt on every render.
  const badges = useMemo(
    () =>
      buildFilterBadges({
        allLabel,
        builtInFilters,
        savedFilters: savedFiltersState.filters,
      }),
    [allLabel, builtInFilters, savedFiltersState.filters],
  );

  // Hook call must stay above the early return (rules of hooks).
  const {
    addControlRef,
    containerRef,
    hiddenBadges,
    menuButtonRef,
    registerBadgeElement,
    visibleBadges,
  } = useBadgeRowOverflow({ badges, selectedFilterId });

  const canShowSavedFilters = config.savedFilters?.enabled;
  if (!canShowSavedFilters) return null;

  function handleCreateSubmit(name: string) {
    const trimmedName = name.trim();
    if (!trimmedName || createSavedFilterMutation.isPending) return;

    if (trimmedName.length > SAVED_FILTER_NAME_MAX_LENGTH) {
      ToastService.error([t(($) => $.savedFilters.create.notifications.error)]);
      return;
    }

    createSavedFilterMutation.mutate({
      filters: [],
      name: trimmedName,
      type: config.type,
    });
  }

  function handleRenameSubmit(name: string) {
    const trimmedName = name.trim();
    if (!editingFilter || !trimmedName || updateSavedFilterMutation.isPending)
      return;

    if (trimmedName.length > SAVED_FILTER_NAME_MAX_LENGTH) {
      ToastService.error([t(($) => $.savedFilters.edit.errorToast)]);
      return;
    }

    updateSavedFilterMutation.mutate({
      expectedVersion: editingFilter.version,
      name: trimmedName,
      savedFilterId: editingFilter.id,
      type: editingFilter.type,
    });
  }

  function handleDeleteConfirm() {
    if (!editingFilter || deleteSavedFilterMutation.isPending) return;

    deleteSavedFilterMutation.mutate({
      savedFilterId: editingFilter.id,
      type: editingFilter.type,
    });
  }

  const deleteAction = editingFilter
    ? {
        cancelText: t(($) => $.savedFilters.edit.delete.dialog.cancel.text),
        confirmText: t(($) => $.savedFilters.edit.delete.dialog.confirm.text),
        description: t(($) => $.savedFilters.edit.delete.dialog.description, {
          name: editingFilter.name,
        }),
        isPending: deleteSavedFilterMutation.isPending,
        onConfirm: handleDeleteConfirm,
        text: t(($) => $.savedFilters.edit.delete.text),
        title: t(($) => $.savedFilters.edit.delete.dialog.title),
      }
    : undefined;

  return (
    <Fragment>
      <div ref={containerRef} className="flex items-end gap-2 overflow-hidden">
        {visibleBadges.map((badge) => {
          const state = getFilterBadgeState({
            badge,
            isDirty,
            resultCountsByFilterId,
            savedFilterIds,
            selectedFilterId,
          });

          return (
            <div
              key={badge.id}
              ref={registerBadgeElement(badge.id)}
              className="shrink-0"
            >
              <SavedFilterBadge
                badge={badge}
                editAriaLabel={t(($) => $.savedFilters.edit.ariaLabel)}
                state={state}
                unsavedChangesLabel={t(($) => $.savedFilters.unsavedChanges)}
                onEdit={setEditingFilter}
                onSelect={(selectedBadge) => {
                  store.trigger.selectFilter({
                    baseline: selectedBadge.baseline,
                    filterId: selectedBadge.id,
                  });
                }}
              />
            </div>
          );
        })}
        {hiddenBadges.length > 0 && (
          <SavedFilterOverflowMenu
            badges={hiddenBadges}
            buttonAriaLabel={t(
              ($) => $.savedFilters.overflowMenu.button.ariaLabel,
            )}
            noResultsLabel={t(($) => $.savedFilters.overflowMenu.noResults)}
            searchPlaceholder={t(
              ($) => $.savedFilters.overflowMenu.searchInput.placeholder,
            )}
            wrapperRef={menuButtonRef}
            onSelect={(selectedBadge) => {
              store.trigger.selectFilter({
                baseline: selectedBadge.baseline,
                filterId: selectedBadge.id,
              });
            }}
          />
        )}
        <div
          ref={addControlRef}
          className="inline-flex h-9 shrink-0 items-center"
        >
          <IconButton
            aria-label={t(($) => $.savedFilters.create.addButton.ariaLabel)}
            className="text-tiga-text-default"
            size="sm"
            onClick={() => {
              setIsCreateDialogOpen(true);
            }}
          >
            <Icon metaphor="add" className="size-4.5" />
          </IconButton>
        </div>
      </div>
      {/* Sits outside the badge container, which clips its overflow. Without a
          signal here a failed load is indistinguishable from having no saved
          filters at all. */}
      {savedFiltersState.error ? (
        <p
          className="text-sm text-tiga-text-lighter"
          data-testid="universal-filters-saved-filters-error"
        >
          {t(($) => $.savedFilters.loadError)}
        </p>
      ) : null}
      <SavedFilterNameDialog
        isOpen={isCreateDialogOpen}
        isPending={createSavedFilterMutation.isPending}
        submitText={t(($) => $.savedFilters.create.submit.text)}
        onClose={() => {
          setIsCreateDialogOpen(false);
        }}
        onSubmit={handleCreateSubmit}
      />
      <SavedFilterNameDialog
        deleteAction={deleteAction}
        initialName={editingFilter?.name}
        isOpen={Boolean(editingFilter)}
        isPending={updateSavedFilterMutation.isPending}
        submitText={t(($) => $.savedFilters.edit.save.text)}
        onClose={() => {
          setEditingFilter(undefined);
        }}
        onSubmit={handleRenameSubmit}
      />
    </Fragment>
  );
}
